import React, { useState } from "react";
import { CheckCircle2, XCircle, Smartphone, Sparkles, ArrowRight, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "../lib/api";

const quizSession = () => {
    let id = sessionStorage.getItem("flex_quiz_session");
    if (!id) { id = `q_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`; sessionStorage.setItem("flex_quiz_session", id); }
    return id;
};
const track = (event, plan_id) => api.post("/quiz/event", { session_id: quizSession(), event, plan_id }).catch(() => {});

// US eSIM-capable models (must also be carrier-unlocked)
const ESIM_PHONES = {
    Apple: ["iPhone 16 / 16 Plus / 16 Pro / 16 Pro Max", "iPhone 16e", "iPhone 15 series", "iPhone 14 series", "iPhone 13 series", "iPhone 12 series", "iPhone 11 series", "iPhone SE (2nd & 3rd gen)", "iPhone XS / XS Max / XR"],
    Samsung: ["Galaxy S25 series", "Galaxy S24 series", "Galaxy S23 series", "Galaxy S22 series", "Galaxy S21 series", "Galaxy S20 series", "Galaxy Z Fold 3 – 6", "Galaxy Z Flip 3 – 6", "Galaxy Note 20 series", "Galaxy A54 5G / A35 5G"],
    Google: ["Pixel 9 series", "Pixel 8 / 8 Pro / 8a", "Pixel 7 / 7 Pro / 7a", "Pixel 6 / 6 Pro / 6a", "Pixel 5 / 5a", "Pixel 4 / 4 XL / 4a", "Pixel 3 / 3 XL / 3a"],
    Motorola: ["Razr+ / Razr (2023 & 2024)", "Edge (2022 – 2024)", "Edge+ (2023)", "Moto G Power 5G (2024)", "Moto G Stylus 5G (2024)"],
};
const NOT_SUPPORTED = { Apple: ["iPhone X or older"], Samsung: ["Galaxy S10 or older", "Most Galaxy A-series under A35"], Google: ["Pixel 2 or older"], Motorola: ["Moto E / older Moto G models"] };

export const PhoneCheck = () => {
    const [brand, setBrand] = useState("");
    const [model, setModel] = useState("");
    const ok = model && !model.startsWith("no:");
    const options = brand && ESIM_PHONES[brand] ? [...ESIM_PHONES[brand].map((m) => ({ v: m, l: m })), ...NOT_SUPPORTED[brand].map((m) => ({ v: `no:${m}`, l: m }))] : [];
    const sel = "border border-[#DDD6FE] rounded-full h-11 px-4 bg-white text-sm outline-none focus:border-[#4C1D95] w-full";
    return (
        <div className="bg-white border border-[#DDD6FE] rounded-2xl p-6" data-testid="phone-check">
            <div className="flex items-center gap-2"><Smartphone className="w-5 h-5 text-emerald-deep" /><p className="font-medium">Will my phone work?</p></div>
            <p className="text-sm text-zinc-600 mt-1">Pick your phone — we'll tell you instantly if it supports FlexLine eSIM.</p>
            <div className="grid sm:grid-cols-2 gap-3 mt-4">
                <select value={brand} onChange={(e) => { setBrand(e.target.value); setModel(""); }} className={sel} data-testid="phone-check-brand">
                    <option value="">Brand…</option>
                    {Object.keys(ESIM_PHONES).map((b) => <option key={b} value={b}>{b}</option>)}
                    <option value="Other">Other brand</option>
                </select>
                <select value={model} onChange={(e) => setModel(e.target.value)} disabled={!brand || brand === "Other"} className={sel} data-testid="phone-check-model">
                    <option value="">Model…</option>
                    {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                </select>
            </div>
            {brand === "Other" && (
                <p className="mt-4 text-sm text-zinc-700" data-testid="phone-check-other">On Android, open <b>Settings → Network &amp; internet → SIMs</b>. If you see "Add eSIM" or "Download a SIM", you're good to go.</p>
            )}
            {model && (
                <div className={`mt-4 flex gap-2 rounded-xl p-4 text-sm ${ok ? "bg-[#ECFDF5] text-[#065F46]" : "bg-red-50 text-red-700"}`} data-testid="phone-check-result">
                    {ok ? <CheckCircle2 className="w-5 h-5 shrink-0" /> : <XCircle className="w-5 h-5 shrink-0" />}
                    <span>{ok ? <><b>Yes — {model} supports eSIM.</b> Just make sure it's carrier-unlocked (your old carrier can confirm) before you pay.</>
                        : <><b>Sorry — {model.slice(3)} doesn't support eSIM.</b> You'll need a newer phone, or try our <a href="/second-number" className="underline font-medium">second phone number</a> app instead.</>}</span>
                </div>
            )}
        </div>
    );
};

const QUESTIONS = [
    { q: "How do you mostly get online?", a: [{ l: "Wi-Fi almost always", s: 0 }, { l: "Mix of Wi-Fi and mobile data", s: 1 }, { l: "Mobile data most of the day", s: 2 }] },
    { q: "Do you stream video or music away from Wi-Fi?", a: [{ l: "Rarely", s: 0 }, { l: "Sometimes", s: 1 }, { l: "Every day", s: 2 }] },
    { q: "Do you use your phone as a hotspot?", a: [{ l: "Never", s: 0 }, { l: "Once in a while", s: 1 }, { l: "Often", s: 2 }] },
];

const recommend = (answers) => {
    const score = answers.reduce((a, b) => a + b, 0);
    if (answers[0] === 0 && score <= 1) return { id: "flex_payg", why: "You're on Wi-Fi most of the time, so you'll only pay for the little data you use." };
    if (score <= 2) return { id: "flex_1gb", why: "Light data use — 1 GB covers email, maps and social, at the lowest fixed price." };
    if (score <= 4) return { id: "flex_3gb", why: "A healthy mix of streaming and browsing — 3 GB with hotspot fits most people." };
    return { id: "flex_5gb", why: "Heavy streaming and hotspot use — 5 GB gives you room to spare." };
};

export const PlanQuiz = ({ plans, onChoose }) => {
    const [answers, setAnswers] = useState([]);
    const step = answers.length;
    const done = step === QUESTIONS.length;
    const rec = done ? recommend(answers) : null;
    const plan = rec && plans.find((p) => p.id === rec.id);
    React.useEffect(() => { if (rec) track("result", rec.id); }, [done]); // eslint-disable-line react-hooks/exhaustive-deps
    return (
        <div className="bg-[#2E1065] text-white rounded-2xl p-6" data-testid="plan-quiz">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-[#FCD34D]" /><p className="font-medium">Not sure which plan? Take the 3-question quiz</p></div>
                {step > 0 && <button onClick={() => setAnswers([])} className="text-xs text-[#DDD6FE] flex items-center gap-1 hover:text-white" data-testid="plan-quiz-restart"><RotateCcw className="w-3.5 h-3.5" /> Restart</button>}
            </div>
            {!done && (
                <div className="mt-4" data-testid={`plan-quiz-step-${step}`}>
                    <p className="text-sm text-[#DDD6FE] font-mono">Question {step + 1} of {QUESTIONS.length}</p>
                    <p className="font-display text-xl mt-1">{QUESTIONS[step].q}</p>
                    <div className="mt-3 grid sm:grid-cols-3 gap-2">
                        {QUESTIONS[step].a.map((o, i) => (
                            <button key={o.l} onClick={() => setAnswers([...answers, o.s])} data-testid={`plan-quiz-answer-${step}-${i}`}
                                className="text-left rounded-xl border border-white/20 px-4 py-3 text-sm hover:bg-white/10 hover:border-[#FCD34D] transition-colors">{o.l}</button>
                        ))}
                    </div>
                </div>
            )}
            {done && plan && (
                <div className="mt-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid="plan-quiz-result">
                    <div>
                        <p className="text-sm text-[#FCD34D] font-mono uppercase">We recommend</p>
                        <p className="font-display text-2xl" data-testid="plan-quiz-recommended">{plan.name} — ${plan.price}/mo</p>
                        <p className="text-sm text-[#DDD6FE] mt-1">{rec.why}</p>
                    </div>
                    <Button onClick={() => { track("choose", plan.id); localStorage.setItem("flex_quiz_plan", plan.id); onChoose(plan.id); }} data-testid="plan-quiz-choose" className="bg-[#D97706] hover:bg-[#B45309] text-white rounded-full h-11 px-6 shrink-0">
                        Choose {plan.name} <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                </div>
            )}
        </div>
    );
};
